import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook } from 'lucide-react';

const CorporateFooter = () => {
  const year = new Date().getFullYear();

  const columns = [
    {
      title: 'Company',
      items: [
        { name: 'About', href: '#about' },
        { name: 'Leadership', href: '#leadership' },
        { name: 'Investor Relations', href: '/investor' },
        { name: 'Contact', href: 'mailto:info@sebasalliance.com' },
      ],
    },
    {
      title: 'Platforms',
      items: [
        { name: 'Caribbean Payments Alliance', href: 'https://caribbeanpaymentsalliance.com', ext: true },
        { name: 'CloudSync Pro', href: 'https://csyncpro.com', ext: true },
        { name: 'BahamasPay', href: 'https://bahamaspay.com', ext: true },
        { name: 'APC Estate Flow', href: 'https://apcestateflow.com', ext: true },
      ],
    },
    {
      title: 'Solutions',
      items: [
        { name: 'Payment Integration', href: 'https://caribbeanpaymentsalliance.com', ext: true },
        { name: 'Hotel Payments', href: 'https://csyncpro.com', ext: true },
        { name: 'Business Formation', href: 'https://bahamaspay.com', ext: true },
        { name: 'Real Estate Workflow', href: 'https://apcestateflow.com', ext: true },
      ],
    },
    {
      title: 'Support',
      items: [
        { name: 'Helpdesk', href: 'https://helpdesk.sebasalliance.com', ext: true },
        { name: 'Events', href: 'https://events.sebasalliance.com', ext: true },
        { name: 'Merchant Support', href: 'mailto:ecommerce@sebasalliance.net' },
        { name: 'Contact Sales', href: 'mailto:info@sebasalliance.com' },
        { name: 'Portfolio', href: '/portfolio' },
        { name: 'Payment Center', href: '/payment-center' },
      ],
    },
    {
      title: 'Legal',
      items: [
        { name: 'Terms of Service', href: '/terms' },
        { name: 'Privacy Policy', href: '/privacy' },
        { name: 'Return & Refund Policy', href: '/refund-policy' },
        { name: 'KYC / AML Policy', href: '/kyc-aml' },
      ],
    },
  ];

  const offices = [
    { city: 'Fort Lauderdale, United States', addr: '101 Tower NE 3rd Avenue, Suite 1500\nFt. Lauderdale, FL 33301', phone: '1-877-268-4779' },
    { city: 'Nassau, Bahamas', addr: 'Victoria House, Rosetta Street\nNassau, Bahamas', phone: '242-698-1061' },
    { city: 'London, United Kingdom', addr: 'Longcroft House, 2/8 Victoria Avenue\nLondon City, United Kingdom', phone: '+44-20-3695-1294' },
  ];

  return (
    <footer id="contact" className="bg-[#0a1628] text-white border-t border-[#C9A227]/20" data-testid="corporate-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-10">
        {/* Top — brand + tagline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-14 border-b border-white/8">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img src="/logos/sag-logo.png" alt="SAG" className="h-12 w-auto" />
              <div>
                <div className="text-white text-base font-medium">Sebastian Alliance Group LLC</div>
                <div className="text-[#C9A227] text-[10px] tracking-[0.25em] uppercase">Corporate Technology Group</div>
              </div>
            </div>
            <p className="text-white/50 text-sm leading-relaxed max-w-md mb-6">
              Digital Infrastructure for Commerce, Finance, Payments &amp; Innovation. A corporate technology group
              building secure digital platforms for businesses, financial institutions, merchants, hotels, real estate,
              and emerging markets.
            </p>
            <div className="flex gap-3">
              {[Linkedin, Twitter, Facebook].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 border border-white/20 hover:border-[#C9A227] hover:bg-[#C9A227]/10 flex items-center justify-center text-white/50 hover:text-[#C9A227] transition-all">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* 4 column nav */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {columns.map((col) => (
              <div key={col.title}>
                <h5 className="text-[#C9A227] text-[10px] tracking-[0.3em] uppercase font-semibold mb-5">{col.title}</h5>
                <ul className="space-y-3">
                  {col.items.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        target={item.ext ? '_blank' : '_self'}
                        rel={item.ext ? 'noopener noreferrer' : ''}
                        className="text-white/55 hover:text-white text-[13px] transition-colors"
                      >
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Offices */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-10 border-b border-white/8">
          {offices.map((o) => (
            <div key={o.city}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-3.5 h-3.5 text-[#C9A227]" />
                <span className="text-white text-[11px] tracking-[0.2em] uppercase font-semibold">{o.city}</span>
              </div>
              <p className="text-white/50 text-xs leading-relaxed whitespace-pre-line mb-2">{o.addr}</p>
              <a href={`tel:${o.phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9A227] text-xs transition-colors">
                <Phone className="w-3 h-3" />
                {o.phone}
              </a>
            </div>
          ))}
        </div>

        {/* Email + bottom */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <a href="mailto:info@sebasalliance.com" className="inline-flex items-center gap-2 text-white/70 hover:text-[#C9A227] text-sm transition-colors">
            <Mail className="w-4 h-4" />
            info@sebasalliance.com
          </a>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] tracking-wider" data-testid="footer-legal-links">
            <a href="/terms" className="text-white/50 hover:text-[#C9A227] transition-colors">Terms</a>
            <span className="text-white/20">·</span>
            <a href="/privacy" className="text-white/50 hover:text-[#C9A227] transition-colors">Privacy</a>
            <span className="text-white/20">·</span>
            <a href="/refund-policy" className="text-white/50 hover:text-[#C9A227] transition-colors">Refunds</a>
            <span className="text-white/20">·</span>
            <a href="/kyc-aml" className="text-white/50 hover:text-[#C9A227] transition-colors">KYC / AML</a>
          </div>
          <div className="text-white/30 text-[11px] tracking-wider">
            © {year} Sebastian Alliance Group LLC. All rights reserved.
          </div>
        </div>

        {/* Powered by — gold strip */}
        <div className="mt-8 pt-6 border-t border-[#C9A227]/20 text-center">
          <p className="text-[#C9A227] text-[10px] tracking-[0.3em] uppercase font-semibold">
            Powered by Sebastian Alliance Group LLC
          </p>
        </div>
      </div>
    </footer>
  );
};

export default CorporateFooter;
